import React, { useState, useEffect } from "react";
import { debounce } from "lodash";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { Layout, Input, Spin, Row, Col, Select, Space, Typography, Table, Checkbox, Tooltip } from "antd";
import { SearchOutlined, LoadingOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import { compose } from "redux";
import Highlighter from "react-highlight-words";
import { withTranslation } from "react-i18next";
import { MultiSelectDrugSearch } from "@tailormed/common-client/component/MultiSelectDrugSearch";

import { selectAssistancePrograms, selectIsFetching, selectUser } from "../store/selector";
import { MultiSelect } from "../component/customComponent/CustomSelect";
import { capitalizeFirstLetter, isEmpty, titleCase } from "../utils/string";
import { parseFundsMaxToDisplay } from "../utils/parse";
import { ApiNames, searchDiagnosisIcdsAPI, searchDiagnosisNamesAPI, searchRxnormDrugsAPI } from "../api/api";
import ACTIONS from "../store/action";
import NoProgramsFoundIcon from "../assets/svg/NoProgramsFoundIcon";
import { SORT_DIRECTIONS } from "../constant/sort";
import { nullSort, stringSort } from "../utils/sort";
import { GRAY, NEW_BLUE, WARNING_700, SUCCESS_700, PRIMARY_700, ERROR_700 } from "../constant/colors";
import { States } from "../constant/states";
import { defaultFilters } from "../constant/filters";
import { ShorterTextWithTooltip } from "../component/ShorterTextWithTooltip";
import { AssistanceProgramModal } from "../component/AssistanceProgramModal";

import ClearIcon from "../assets/svg/ClearIcon";
import { AP_STATUSES } from "../constant/applications";

const { Content } = Layout;
const { Option } = Select;
const { Text } = Typography;

const ALL = "All";
const PAGE_SIZE = 20;

const getFundTypes = (t) => [
  {
    text: t("assistance_programs.program_types.manufacturer_copay"),
    value: "manufacturer_copay"
  },
  {
    text: t("assistance_programs.program_types.manufacturer_pap"),
    value: "manufacturer_pap"
  },
  {
    text: t("assistance_programs.program_types.manufacturer_other"),
    value: "manufacturer_other"
  },
  {
    text: t("assistance_programs.program_types.foundation_copay"),
    value: "foundation_copay"
  },
  {
    text: t("assistance_programs.program_types.foundation_premium"),
    value: "foundation_premium"
  },
  {
    text: t("assistance_programs.program_types.ancillary_living"),
    value: "ancillary_living"
  },
  {
    text: t("assistance_programs.program_types.support_adherence"),
    value: "support_adherence"
  },
  {
    text: t("assistance_programs.program_types.government_plans"),
    value: "government_plans"
  },
  {
    text: t("assistance_programs.program_types.discount"),
    value: "discount"
  }
];

const colorByStatus = Object.freeze({
  [AP_STATUSES.closed]: ERROR_700,
  [AP_STATUSES.waitList]: WARNING_700,
  [AP_STATUSES.open]: SUCCESS_700,
  [AP_STATUSES.waitListOnly]: PRIMARY_700
});
const AP_STATUSES_LABELS = Object.freeze({
  ...AP_STATUSES,
  [AP_STATUSES.waitListOnly]: "Processing Waitlist",
  [AP_STATUSES.waitList]: "Accepting Waitlist"
});

const AP_STATUSES_TOOLTIP_CONTENT = Object.freeze({
  [AP_STATUSES.waitListOnly]: "Processing Patients on the Waitlist",
  [AP_STATUSES.waitList]: "Accepting Patients to the Waitlist"
});

const InsuranceTypes = Object.freeze({
  COMMERCIAL: "Commercial",
  FEDERAL: "Federal",
  MEDICAID: "Medicaid",
  MEDICARE: "Medicare",
  UNINSURED: "Uninsured"
});

const BaseToolTip = (props) => {
  return (
    <Tooltip
      placement="top"
      title={<Text>{props.title}</Text>}
      id={props.toolTipId ? props.toolTipId : null}
      color={"white"}
    >
      <QuestionCircleOutlined
        id={props.iconId ? props.iconId : null}
        style={{
          height: "100%",
          color: GRAY,
          position: "relative",
          left: props.marginLeft ? props.marginLeft : null,
          cursor: "pointer",
          ...props.iconStyles
        }}
      />
    </Tooltip>
  );
};

const ApStatusTag = ({ record }) => {
  const textValue = capitalizeFirstLetter(
    AP_STATUSES_LABELS[record.status] ? AP_STATUSES_LABELS[record.status] : record.status
  );
  const toolTipText = capitalizeFirstLetter(
    AP_STATUSES_TOOLTIP_CONTENT[record.status] ? AP_STATUSES_TOOLTIP_CONTENT[record.status] : record.status
  );

  const withToolTip = [AP_STATUSES.waitList, AP_STATUSES.waitListOnly].includes(record.status);
  return (
    <div>
      {withToolTip ? (
        <Tooltip title={toolTipText} placement={"topLeft"}>
          <Text
            style={{
              color: colorByStatus[record.status],
              fontWeight: 600,
              wordBreak: "keep-all"
            }}
          >
            {textValue}
          </Text>
        </Tooltip>
      ) : (
        <Text
          style={{
            color: colorByStatus[record.status],
            fontWeight: 600,
            wordBreak: "keep-all"
          }}
        >
          {textValue}
        </Text>
      )}
    </div>
  );
};

const AssistanceProgramsComponent = ({
  t,
  isFetching,
  assistancePrograms,
  searchAssistanceProgramAct,
  selectAssistanceProgramAct,
  clearAssistancePrograms
}) => {
  const [searchInput, setSearchInput] = useState("");

  const [filters, setFilters] = useState(defaultFilters);

  const [diagnosisSearchResults, setDiagnosisSearchResults] = useState([]);
  const [diagnosisIcdsSearchResults, setDiagnosisIcdsSearchResults] = useState([]);

  const [isFetchingDiagnosis, setIsFetchingDiagnosis] = useState(false);
  const [isFetchingDiagnosisIcds, setIsFetchingDiagnosisIcds] = useState(false);

  const isFilterChanged = () =>
    filters.programTypeFilters.length ||
    filters.diagnosisNames.length ||
    filters.diagnosisCodes.length ||
    filters.rxnormDrugsFilter.length ||
    filters.insuranceTypes.length ||
    filters.states.length ||
    filters.openedFunds === true;

  const isClearActive = isFilterChanged() || searchInput !== "";

  useEffect(() => {
    if (isClearActive) {
      const dataToSend = { ...filters };

      Object.keys(filters).forEach((key) => {
        dataToSend[key] = filters[key]?.length ? filters[key] : key === "openedFunds" ? filters[key] : null;
      });

      searchAssistanceProgramAct(searchInput, dataToSend);
    } else {
      clearAssistancePrograms();
    }
  }, [searchInput, filters]);

  const resetAllFilters = () => {
    setFilters({ ...defaultFilters });
  };

  const onDiagnosisSearch = async (text) => {
    if (isEmpty(text)) return;

    try {
      setIsFetchingDiagnosis(true);
      const result = await searchDiagnosisNamesAPI(text);
      const { data } = result;
      setDiagnosisSearchResults(data.data);
    } catch (error) {
      console.log(error);
    } finally {
      setIsFetchingDiagnosis(false);
    }
  };

  const onDiagnosisIcdsSearch = async (text) => {
    if (isEmpty(text)) return;

    try {
      setIsFetchingDiagnosisIcds(true);
      const result = await searchDiagnosisIcdsAPI(text);
      const { data } = result;
      setDiagnosisIcdsSearchResults(data.data);
    } catch (error) {
      console.log(error);
    } finally {
      setIsFetchingDiagnosisIcds(false);
    }
  };

  const columns = [
    {
      title: <Text id="sort_column_by_name">{t("assistance_programs.table.name")}</Text>,
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <Highlighter
          unhighlightStyle={{ fontWeight: 600 }}
          highlightStyle={{
            backgroundColor: "rgba(255, 0, 124, 0.1)",
            padding: 0
          }}
          searchWords={[searchInput]}
          autoEscape
          textToHighlight={`${record.name}`}
        />
      ),
      sortDirections: SORT_DIRECTIONS,
      sorter: (a, b, sortDirection) => stringSort(a.name, b.name, sortDirection),
      onCell: (record) => ({
        onClick: () => {
          const { id } = record;
          selectAssistanceProgramAct(id);
        }
      })
    },
    {
      title: <Text id="sort_column_by_provided_by">{t("assistance_programs.table.provided_by")}</Text>,
      dataIndex: "providedBy",
      key: "providedBy",
      sorter: (a, b, sortDirection) => stringSort(a.providedBy, b.providedBy, sortDirection),
      sortDirections: SORT_DIRECTIONS,
      onCell: (record) => ({
        onClick: () => {
          const { id } = record;
          selectAssistanceProgramAct(id);
        }
      })
    },
    {
      title: <Text id="sort_column_by_type">{t("assistance_programs.table.type")}</Text>,
      dataIndex: "type",
      key: "type",
      sorter: (a, b, sortDirection) => stringSort(a.type, b.type, sortDirection),
      sortDirections: SORT_DIRECTIONS,
      onCell: (record) => ({
        onClick: () => {
          const { id } = record;
          selectAssistanceProgramAct(id);
        }
      })
    },
    {
      title: t("assistance_programs.table.Insurance"),
      dataIndex: "insuranceTypes",
      key: "insuranceTypes",
      render: (text, record) => `${record.insuranceTypes.join(", ")}`,
      onCell: (record) => ({
        onClick: () => {
          const { id } = record;
          selectAssistanceProgramAct(id);
        }
      })
    },
    {
      title: t("assistance_programs.table.diagnosis"),
      dataIndex: "requiredDiagnosis",
      key: "requiredDiagnosis",
      render: (text, record) =>
        record.requiredDiagnosis ? (
          <ShorterTextWithTooltip text={record.requiredDiagnosis.join(", ")} maxLength={50} />
        ) : (
          ""
        ),
      onCell: (record) => ({
        onClick: () => {
          const { id } = record;
          selectAssistanceProgramAct(id);
        }
      })
    },
    {
      title: <Text id="sort_column_by_grant_amount">{t("assistance_programs.table.grant_amount")}</Text>,
      dataIndex: "fundsMax",
      key: "fundsMax",
      width: "145px",
      render: (text, record) => <Text>{parseFundsMaxToDisplay(record)}</Text>,
      sorter: (a, b, sortDirection) => nullSort(a.fundsMax, b.fundsMax, sortDirection, () => a.fundsMax - b.fundsMax),
      sortDirections: SORT_DIRECTIONS,
      onCell: (record) => ({
        onClick: () => {
          const { id } = record;
          selectAssistanceProgramAct(id);
        }
      })
    },
    {
      title: <Text id="sort_column_by_status">{t("assistance_programs.table.status")}</Text>,
      dataIndex: "status",
      key: "status",
      width: "100px",
      render: (text, record) => <ApStatusTag record={record} />,
      sorter: (a, b, sortDirection) => stringSort(a.status, b.status, sortDirection),
      sortDirections: SORT_DIRECTIONS,
      onCell: (record) => ({
        onClick: () => {
          const { id } = record;
          selectAssistanceProgramAct(id);
        }
      })
    }
  ];

  const onChangeSelectedDrugs = (selectedDrugs) => {
    setFilters({
      ...filters,
      rxnormDrugsFilter: selectedDrugs.map(({ id, tmDisplayName, tty, ttyCui, pinCui = null }) => {
        const value = pinCui ? `${tty}_${ttyCui}_${pinCui}` : `${tty}_${ttyCui}`;
        return {
          id,
          text: tmDisplayName,
          value,
          label: tmDisplayName,
          tmDisplayName,
          tty
        };
      })
    });
  };

  const onDrugSearchClear = () =>
    setFilters({
      ...filters,
      rxnormDrugsFilter: []
    });

  const searchRxnormDrugs = async (searchTerm) => {
    const response = await searchRxnormDrugsAPI(searchTerm);
    return response?.data?.rxnormDrugs || [];
  };

  const {
    openedFunds,
    rxnormDrugsFilter,
    programTypeFilters: types,
    diagnosisCodes,
    diagnosisNames,
    states,
    insuranceTypes
  } = filters;
  return (
    <Content style={{ display: "flex", flexDirection: "column" }}>
      <Text style={{ color: "#7f7f7f", fontSize: 20, fontWeight: 600 }}>{t("main_subtitle")}</Text>
      <Row style={{ paddingTop: 20 }}>
        <Col span={5}>
          <div style={{ padding: "20px 20px 30px 20px", boxShadow: "0px 6px 8px rgba(0, 0, 0, 0.15)" }}>
            <Text style={{ color: NEW_BLUE, fontSize: 16, fontWeight: 600 }}>
              {t("assistance_programs.filters.title")}
            </Text>
            <div
              onClick={() => isClearActive && !isFetching && resetAllFilters()}
              style={{
                display: "flex",
                justifyContent: "flex-end",
                padding: "8px 16px 0px 0px",
                opacity: !isClearActive || isFetching ? "0.5" : undefined,
                cursor: !isClearActive || isFetching ? "not-allowed" : "pointer"
              }}
            >
              <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                <ClearIcon />
                <Text strong level={5} style={{ color: NEW_BLUE, paddingLeft: 4 }}>
                  {t("assistance_programs.filters.clear_all")}
                </Text>
              </div>
            </div>
            <Space size={30} direction={"vertical"} style={{ width: "100%", paddingTop: 20 }}>
              <div id="show_only_open_funds">
                <Checkbox
                  checked={openedFunds}
                  onChange={(e) => setFilters({ ...filters, openedFunds: e.target.checked })}
                >
                  {t("assistance_programs.filters.show_only")}
                </Checkbox>
              </div>
              <Space size={0} direction={"vertical"} style={{ width: "100%" }}>
                <Text strong style={{ color: GRAY }}>
                  {t("assistance_programs.filters.drug_name")}
                </Text>
                <div id="filter_by_drug_name">
                  <MultiSelectDrugSearch
                    placeholder={ALL}
                    displayNameMaxChars={30}
                    onChangeSelectedDrugs={onChangeSelectedDrugs}
                    onClear={onDrugSearchClear}
                    searchRxnormDrugs={searchRxnormDrugs}
                    style={{
                      borderBottom: `1px solid ${GRAY}`,
                      width: "100%"
                    }}
                    bordered={false}
                    notFoundMessage={t("not_found")}
                    value={rxnormDrugsFilter}
                  />
                </div>
              </Space>
              <Space size={0} direction={"vertical"} style={{ width: "100%" }}>
                <div>
                  <Text strong style={{ color: GRAY }}>
                    {t("assistance_programs.filters.fund_type")}
                  </Text>
                  <BaseToolTip
                    title={t("assistance_programs.filters.manufacturer_other_tooltip")}
                    toolTipId="type_filter_popup"
                    iconId="type_filter_popup_icon"
                    iconStyles={{ left: "10px" }}
                  />
                </div>
                <div id="filter_by_fund_type">
                  <MultiSelect
                    value={types}
                    maxTagCount={1}
                    maxTagTextLength={types.length > 1 ? 10 : 16}
                    onChange={(values, records) =>
                      setFilters({
                        ...filters,
                        programTypeFilters: records.map(({ children, value }) => ({ text: children, value }))
                      })
                    }
                    placeholder={ALL}
                  >
                    {getFundTypes(t).map(({ text, value }, index) => (
                      <Option key={index} value={value}>
                        {text}
                      </Option>
                    ))}
                  </MultiSelect>
                </div>
              </Space>
              <Space size={0} direction={"vertical"} style={{ width: "100%" }}>
                <Text strong style={{ color: GRAY }}>
                  {t("assistance_programs.filters.diagnosis_name")}
                </Text>
                <div id="filter_by_diagnosis_name">
                  <MultiSelect
                    value={diagnosisNames}
                    onBlur={() => setDiagnosisSearchResults([])}
                    onChange={(values, records) =>
                      setFilters({
                        ...filters,
                        diagnosisNames: records.map(({ value }) => ({ text: value, value }))
                      })
                    }
                    placeholder={ALL}
                    allowClear={false}
                    showSearch={true}
                    loading={isFetchingDiagnosis}
                    onSearch={debounce(onDiagnosisSearch, 500)}
                    suffixIcon={
                      isFetchingDiagnosis ? <Spin size={"small"} indicator={<LoadingOutlined spin />} /> : <></>
                    }
                    notFoundContent={t("not_found")}
                  >
                    {diagnosisSearchResults.map((d, index) => (
                      <Option key={index} value={d.value}>
                        {d.text}
                      </Option>
                    ))}
                  </MultiSelect>
                </div>
              </Space>
              <Space size={0} direction={"vertical"} style={{ width: "100%" }}>
                <Text strong style={{ color: GRAY }}>
                  {t("assistance_programs.filters.diagnosis_code")}
                </Text>
                <div id="filter_by_diagnosis_code">
                  <MultiSelect
                    value={diagnosisCodes}
                    onBlur={() => setDiagnosisIcdsSearchResults([])}
                    onChange={(values, records) =>
                      setFilters({
                        ...filters,
                        diagnosisCodes: records.map(({ value }) => ({ text: value, value }))
                      })
                    }
                    placeholder={ALL}
                    allowClear={false}
                    showSearch={true}
                    loading={isFetchingDiagnosisIcds}
                    onSearch={debounce(onDiagnosisIcdsSearch, 500)}
                    suffixIcon={
                      isFetchingDiagnosisIcds ? <Spin size={"small"} indicator={<LoadingOutlined spin />} /> : <></>
                    }
                    notFoundContent={t("not_found")}
                  >
                    {diagnosisIcdsSearchResults.map((d, index) => (
                      <Option key={index} value={d.value}>
                        {d.text}
                      </Option>
                    ))}
                  </MultiSelect>
                </div>
              </Space>
              <Space size={0} direction={"vertical"} style={{ width: "100%" }}>
                <Text strong style={{ color: GRAY }}>
                  {t("assistance_programs.filters.insurance_type")}
                </Text>
                <div id="filter_by_insurance_type">
                  <MultiSelect
                    value={insuranceTypes}
                    onChange={(values, records) =>
                      setFilters({
                        ...filters,
                        insuranceTypes: records.map(({ value }) => ({ text: value, value }))
                      })
                    }
                    placeholder={ALL}
                  >
                    {Object.keys(InsuranceTypes).map((insuranceTypeKey, index) => (
                      <Option key={index} value={insuranceTypeKey.toLowerCase()}>
                        {InsuranceTypes[insuranceTypeKey]}
                      </Option>
                    ))}
                  </MultiSelect>
                </div>
              </Space>
              <Space size={0} direction={"vertical"} style={{ width: "100%" }}>
                <Text strong style={{ color: GRAY }}>
                  {t("assistance_programs.filters.allowed_states")}
                </Text>
                <div id="filter_by_allowed_states">
                  <MultiSelect
                    value={states}
                    onChange={(values, records) =>
                      setFilters({
                        ...filters,
                        states: records.map(({ children, value }) => ({ text: children, value }))
                      })
                    }
                    placeholder={ALL}
                    allowClear={true}
                    showSearch={true}
                    notFoundContent={t("not_found")}
                    filterOption={(input, option) => {
                      if (!input) return true;
                      const searchTerm = input.trim().toLowerCase();
                      if (!searchTerm) return true;
                      const stateName = option?.children?.toLowerCase() || "";
                      const stateCode = option?.value?.toLowerCase() || "";
                      return stateName.indexOf(searchTerm) >= 0 || stateCode.indexOf(searchTerm) >= 0;
                    }}
                  >
                    {Object.values(States).map((state, index) => (
                      <Option key={index} value={state.code}>
                        {titleCase(state.name)}
                      </Option>
                    ))}
                  </MultiSelect>
                </div>
              </Space>
            </Space>
          </div>
        </Col>
        <Col span={19} style={{ paddingLeft: 20 }}>
          <div id="search_by_program_name">
            <Input
              allowClear={true}
              suffix={isFetching ? <Spin size="small" /> : <SearchOutlined />}
              placeholder={t("assistance_programs.search_placeholder")}
              onChange={debounce((value) => setSearchInput(value.target.value), 500)}
              style={{ width: "100%", height: 40, borderRadius: 6 }}
            />
          </div>
          <Table
            style={{ paddingTop: 14 }}
            loading={isFetching}
            columns={columns}
            locale={{ emptyText: isClearActive && <NoProgramsFoundIcon /> }}
            dataSource={assistancePrograms.map((assistanceProgram, index) => ({ ...assistanceProgram, key: index }))}
            pagination={{ showSizeChanger: false, defaultPageSize: PAGE_SIZE }}
          />
        </Col>
      </Row>
      <AssistanceProgramModal />
    </Content>
  );
};

const mapStateToProps = createStructuredSelector({
  user: selectUser,
  assistancePrograms: selectAssistancePrograms,
  isFetching: selectIsFetching([ApiNames.AssistanceProgramSearch])
});

const mapDispatchToProps = (dispatch) => ({
  selectAssistanceProgramAct: (id) => dispatch(ACTIONS.selectAssistanceProgramAction(id)),
  searchAssistanceProgramAct: (searchQuery, filters) =>
    dispatch(ACTIONS.searchAssistanceProgramAction(searchQuery, filters)),
  clearAssistancePrograms: () => dispatch(ACTIONS.clearAssistancePrograms())
});

const AssistanceProgramsView = compose(
  withTranslation(),
  connect(mapStateToProps, mapDispatchToProps)
)(AssistanceProgramsComponent);

export { AssistanceProgramsView };
